import {useRef, useState} from 'react';
import {useDispatch} from 'react-redux';

import {useSubscribeToNotificationsQuery} from '@dms/api/appsync';
import {vehicleCatalogueApi} from '@dms/api/sales';
import {notificationTypes} from '@dms/shared';

/**
 * A replacement answers 202 and finishes in a worker, so the list can only be refetched once the
 * completion toast arrives - refetching earlier would return it unchanged.
 *
 * A run is keyed by the make group it was submitted from: both dialogs replace values of a single
 * make, so one key covers the whole table row either way.
 *
 * The timeout is the only handling of a failed run: no failure toast exists, so without it a row
 * would stay disabled forever. Submitting a make twice is safe, the second run reports it was
 * already replaced.
 */
const PROGRESS_TIMEOUT_MS = 45_000;

export function useRemapProgress() {
  const dispatch = useDispatch();
  const [keysInProgress, setKeysInProgress] = useState<string[]>([]);
  const timeoutIdsRef = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const refetchUsage = () =>
    dispatch(vehicleCatalogueApi.util.invalidateTags(['CustomTenantCatalogue']));

  const finish = (key: string) => {
    const timeoutId = timeoutIdsRef.current.get(key);

    if (timeoutId) {
      clearTimeout(timeoutId);
      timeoutIdsRef.current.delete(key);
    }

    setKeysInProgress((keys) => keys.filter((keyInProgress) => keyInProgress !== key));
    refetchUsage();
  };

  const start = (key: string) => {
    setKeysInProgress((keys) => (keys.includes(key) ? keys : [...keys, key]));

    // A second run on the same key restarts the timer; a leftover one would release it early.
    const runningTimeoutId = timeoutIdsRef.current.get(key);

    if (runningTimeoutId) {
      clearTimeout(runningTimeoutId);
    }

    timeoutIdsRef.current.set(
      key,
      setTimeout(() => finish(key), PROGRESS_TIMEOUT_MS)
    );
  };

  useSubscribeToNotificationsQuery((notification) => {
    if (notification.type !== notificationTypes.CUSTOM_CATALOGUE_REMAP_FINISHED) {
      return;
    }

    // Cleared as a whole rather than by key: a run started elsewhere is worth refetching too.
    timeoutIdsRef.current.forEach(clearTimeout);
    timeoutIdsRef.current.clear();
    setKeysInProgress([]);
    refetchUsage();
  });

  return {keysInProgress, start};
}
